/* eslint-disable i18next/no-literal-string */

const staticSemanticColorNames = [
  'white10',
  'white25',
  'white30',
  'white40',
  'white53',
  'white60',
  'white70',
  'white80',
  'white90',
  'white100',
  'transparent',
  'black60',
  'criticalPrimary',
  'criticalRed600',
  'criticalRed40',
  'criticalRed30',
  'criticalRed20',
  'criticalRed10',
  'crimson70',
  'criticalRedTint60',
  'criticalRedTint50',
  'criticalRedTint10',
  'redGradient',
  'successPrimary',
  'successBackground',
  'successGreen20',
  'seafoam30',
  'seafoam40',
  'neutralGray0',
  'neutralGray14',
  'neutralGray26',
  'neutralGray38',
  'neutralGray74',
  'neutralGray82',
  'neutralGray94',
  'neutralGray96',
  'neutralGray98',
  'blueGray200',
  'blueGray75',
  'slate100',
  'slate50',
  'bluePrimary',
  'blueShade10',
  'blueShade20',
  'blueShade30',
  'blueShade40',
  'blueTint10',
  'blueTint15',
  'blueTint20',
  'blueTint30',
  'blueTint40',
  'anchorTint20',
  'anchorTint55',
  'anchorTint60',
  'anchorShade10',
  'anchorShade20',
  'anchorShade30',
  'anchorShade40',
  'anchorShade20Transparent60',
  'anchorShade20Transparent90',
  'anchorShade30Transparent40',
  'anchorShade30Transparent60',
  'anchorShade30Transparent90',
  'successPrimaryRGB',
  'successBackgroundRGBA0',
  'successBackgroundRGBA100',
  'seafoam30RGBA0',
  'seafoam30RGBA60',
] as const;

const customSemanticColorNames = [
  // whites
  'white100ToAnchor20',
  'white90ToAnchor30tr90',
  'white60ToAnchor30tr60',
  'white60ToAnchor20tr60',
  'white60ToAnchor20',
  'white60ToAnchor10',
  'white40ToBlack40',
  'white40ToAnchor30tr40',
  'white40ToAnchor20tr60',
  'white30ToAnchor20tr60',
  // reds (critical colors)
  'redPrimaryToRed10',
  'redPrimaryToCrimson70',
  'red600ToRedPrimary',
  'red600ToCrimson70',
  'red20ToRedPrimary',
  'red20ToCrimson70',
  'red20ToRedTint10',
  'red20ToWhite',
  'redTint50ToRed30',
  // greens (success colors)
  'green20ToGreenPrimary',
  'greenBackgroundToSeafoam30',
  'greenBackgroundToSeafoam30RGBA0',
  'greenBackgroundToSeafoam30RGBA100',
  // grays (neutral colors)
  'gray98ToAnchor30',
  'gray98ToAnchor20tr60',
  'gray94ToAnchor40',
  'gray94ToAnchor20',
  'gray94ToAnchor10',
  'gray94ToTransparent',
  'gray74ToAnchorTint20',
  'gray38ToWhite',
  'gray26ToGray94',
  'gray14ToWhite',
  // blues
  'bluePrimaryToWhite',
  'blue20ToBluePrimary',
  'blue20ToBlueTint10',
  'blue20ToWhite',
  'blueTint40ToBlue10',
  'blueTint20ToBlue20',
  'anchorTint60ToAnchor30',
  'anchorTint55ToAnchor40',
  // other
  'neutralPrimaryToWhite',

  // component-specific colors
  'text',
  'textLight',
  'lightHeadlineText',
  'actionButtonText',
  'pageBackground',
  'pageBackgroundColor',
  'pageFooterBackground',
  'copilotPanelBackground',
  'calloutBackground',
  'horizontalLineColor',
  'inputBorderUnfocused',
  'completionCountBackground',
  'criticalRedBadge',
  'deviceAlertTimelineFill',
  // cards
  'cardContentBackground',
  'cardContentBackgroundHovered',
  'cardBorder',
  'cardBorderFocused',
  // dialog
  'dialogBackground',
  'dialogBackgroundColor',
  'dialogBackdrop',
  'dialogOverlayBackground',
  'dialogFooterBackground',
  'dialogCloseIcon',
  'dialogCloseIconHover',
  // list/table
  'listBorder',
  'listBorderFocused',
  'listHeaderBackground',
  'listHeaderText',
  'listFooterBackground',
  'listZeroDataBackground',
  // tags
  'greenTagBackground',
  'yellowTagBackground',
  'redTagBackground',
  'defaultTagBackground',
  'redTagText',
  'greenTagBorder',
  'redTagBorder',
  // icons
  'themeIconDefault',
  'iconCircleBackground',
  // scrollbar
  'customScrollbarThumb',
  'customScrollbarThumbHover',
] as const;

type StaticSemanticColors = Record<(typeof staticSemanticColorNames)[number], string>;
export type CustomSemanticColors = Record<(typeof customSemanticColorNames)[number], string>;
export type AppSemanticColors = StaticSemanticColors & CustomSemanticColors;

export const staticAppColors: StaticSemanticColors = {
  white10: 'rgba(255, 255, 255, 0.1)',
  white25: 'rgba(255, 255, 255, 0.25)',
  white30: 'rgba(255, 255, 255, 0.3)',
  white40: 'rgba(255, 255, 255, 0.4)',
  white53: 'rgba(255, 255, 255, 0.53)',
  white60: 'rgba(255, 255, 255, 0.6)',
  white70: 'rgba(255, 255, 255, 0.7)',
  white80: 'rgba(255, 255, 255, 0.8)',
  white90: 'rgba(255, 255, 255, 0.9)',
  white100: 'rgba(255, 255, 255)',
  transparent: 'rgba(255, 255, 255, 0)',
  black60: 'rgba(0, 0, 0, 0.6)',
  // critical colors
  criticalPrimary: '#EE4062', // criticalRed200, crimson80
  criticalRed600: '#B61E3C',
  criticalRed40: '#3F1011',
  criticalRed30: '#6E0911',
  criticalRed20: '#9F282C', // crimson30
  criticalRed10: '#BC2F34',
  crimson70: '#E94B5C',
  criticalRedTint60: '#FDF6F6',
  criticalRedTint50: '#FFF1EE',
  criticalRedTint10: '#F27089',
  redGradient: 'radial-gradient(circle at top left, #FFB900, #F4364B)',
  // success colors
  successPrimary: '#18BD8A',
  successBackground: '#EBFFF9',
  successGreen20: '#009B50', // seafoam20
  seafoam30: '#00723B',
  seafoam40: '#00402D',
  // neutral grays (aka neutral greys)
  neutralGray0: '#000000',
  neutralGray14: '#242424',
  neutralGray26: '#424242',
  neutralGray38: '#616161',
  neutralGray74: '#BDBDBD',
  neutralGray82: '#D1D1D1',
  neutralGray94: '#F0F0F0',
  neutralGray96: '#F5F5F5', // not part of standard gray palette
  neutralGray98: '#FAFAFA',
  // slate grays (aka blue-gray)
  blueGray200: '#A9B5C0',
  blueGray75: '#D7DCE2',
  slate100: '#D2E2EE',
  slate50: '#FAFDFF',
  // blues (primary and shades 10/20/30 are fluentPalette overrides)
  bluePrimary: '#0078D4', // cobalt70, brand70
  blueShade10: '#106EBE',
  blueShade20: '#005A9E', // brand40
  blueShade30: '#004578',
  blueShade40: '#082338',
  blueTint10: '#2B88D8',
  blueTint15: '#62ABF5',
  blueTint20: '#C7E0F4',
  blueTint30: '#DEECF9',
  blueTint40: '#EFF6FC',
  anchorTint20: '#626C72',
  anchorTint55: '#EBEDEF',
  anchorTint60: '#F6F7F8',
  anchorShade10: '#333A3F',
  anchorShade20: '#2B3135',
  anchorShade30: '#202427',
  anchorShade40: '#111315',
  anchorShade20Transparent60: 'rgba(43, 49, 53, 0.6)',
  anchorShade20Transparent90: 'rgba(43, 49, 53, 0.9)',
  anchorShade30Transparent40: 'rgba(32, 36, 39, 0.4)',
  anchorShade30Transparent60: 'rgba(32, 36, 39, 0.6)',
  anchorShade30Transparent90: 'rgba(32, 36, 39, 0.9)',
  // colors in RGB format (for specific use cases)
  successPrimaryRGB: '24, 189, 138',
  successBackgroundRGBA0: 'rgba(235, 255, 249, 0)',
  successBackgroundRGBA100: 'rgba(235, 255, 249, 1)',
  seafoam30RGBA0: 'rgba(0, 114, 59, 0)',
  seafoam30RGBA60: 'rgba(0, 114, 59, 0.6)',
};

export const createAppSemanticColors = (customAppColors: CustomSemanticColors): AppSemanticColors => ({
  ...staticAppColors,
  ...customAppColors,
});
